import { normalizeColorHex } from '@/lib/utils';
import { mapProjectStatusFromDTO } from '@/modules/projects/mappers/project-status.mapper';

import {
  OverheadRecurrenceFrequencyDTO,
  OverheadStatusDTO,
  OverheadTypeDTO,
} from '../types/dto/overhead.dto';
import { SearchProjectOverheadsFilterRequest } from '../types/dto/request/search-project-overheads.request';
import {
  ProjectOverheadResponse,
  ProjectOverheadsSummaryResponse,
  ProjectOverheadSummaryBucketResponse,
} from '../types/dto/response/project-overhead.response';
import {
  GetOverheadsFilter,
  OverheadListItem,
  OverheadRecurrenceFrequency,
  OverheadsSummary,
  OverheadStatus,
  OverheadSummaryBucket,
  OverheadType,
} from '../types/overhead';

export const mapOverheadTypeFromDTO = (dto: OverheadTypeDTO): OverheadType => {
  switch (dto) {
    case OverheadTypeDTO.INCOME:
      return OverheadType.Income;
    case OverheadTypeDTO.COST:
      return OverheadType.Cost;
  }
};

export const mapOverheadTypeToDTO = (type: OverheadType): OverheadTypeDTO => {
  switch (type) {
    case OverheadType.Income:
      return OverheadTypeDTO.INCOME;
    case OverheadType.Cost:
      return OverheadTypeDTO.COST;
  }
};

export const mapOverheadStatusFromDTO = (dto: OverheadStatusDTO): OverheadStatus => {
  switch (dto) {
    case OverheadStatusDTO.PENDING:
      return OverheadStatus.Pending;
    case OverheadStatusDTO.INVOICED:
      return OverheadStatus.Invoiced;
    case OverheadStatusDTO.PLANNED:
      return OverheadStatus.Planned;
    case OverheadStatusDTO.PAID:
      return OverheadStatus.Paid;
  }
};

export const mapOverheadStatusToDTO = (status: OverheadStatus): OverheadStatusDTO => {
  switch (status) {
    case OverheadStatus.Pending:
      return OverheadStatusDTO.PENDING;
    case OverheadStatus.Invoiced:
      return OverheadStatusDTO.INVOICED;
    case OverheadStatus.Planned:
      return OverheadStatusDTO.PLANNED;
    case OverheadStatus.Paid:
      return OverheadStatusDTO.PAID;
  }
};

/** One-time overheads carry no recurrence on the wire, hence the null. */
export const mapOverheadRecurrenceFrequencyToDTO = (
  frequency: OverheadRecurrenceFrequency
): OverheadRecurrenceFrequencyDTO | null => {
  switch (frequency) {
    case OverheadRecurrenceFrequency.OneTime:
      return null;
    case OverheadRecurrenceFrequency.Weekly:
      return OverheadRecurrenceFrequencyDTO.WEEKLY;
    case OverheadRecurrenceFrequency.Monthly:
      return OverheadRecurrenceFrequencyDTO.MONTHLY;
    case OverheadRecurrenceFrequency.Quarterly:
      return OverheadRecurrenceFrequencyDTO.QUARTERLY;
    case OverheadRecurrenceFrequency.Yearly:
      return OverheadRecurrenceFrequencyDTO.YEARLY;
  }
};

export const mapOverheadFromResponse = (dto: ProjectOverheadResponse): OverheadListItem => ({
  id: dto.id,
  type: mapOverheadTypeFromDTO(dto.type),
  name: dto.name,
  notes: dto.notes,
  date: dto.date,
  amount: Number(dto.amount),
  currencyCode: dto.currencyCode,
  project: {
    id: dto.project.id,
    name: dto.project.name,
    colorHex: normalizeColorHex(dto.project.colorHex),
    logoUrl: dto.project.logoUrl,
    status: dto.project.status ? mapProjectStatusFromDTO(dto.project.status) : null,
  },
  clientName: dto.clientName,
  status: mapOverheadStatusFromDTO(dto.status),
  invoice: dto.invoice,
  paidAt: dto.paidAt,
  seriesId: dto.seriesId,
});

export const mapOverheadsFromResponse = (dtos: ProjectOverheadResponse[]): OverheadListItem[] =>
  dtos.map(mapOverheadFromResponse);

const mapSummaryBucketFromResponse = (
  bucket: ProjectOverheadSummaryBucketResponse
): OverheadSummaryBucket => ({
  amount: Number(bucket.amount),
  count: bucket.count,
});

export const mapOverheadsSummaryFromResponse = (
  dto: ProjectOverheadsSummaryResponse
): OverheadsSummary => ({
  currencyCode: dto.currencyCode,
  incomePending: mapSummaryBucketFromResponse(dto.incomePending),
  incomeInvoiced: mapSummaryBucketFromResponse(dto.incomeInvoiced),
  costPlanned: mapSummaryBucketFromResponse(dto.costPlanned),
  costPaid: mapSummaryBucketFromResponse(dto.costPaid),
});

export const mapOverheadsFilterToRequest = (
  filter: GetOverheadsFilter
): SearchProjectOverheadsFilterRequest => ({
  searchString: filter.searchString,
  dateFrom: filter.dateFrom,
  dateTo: filter.dateTo,
  projectIds: filter.projectIds,
  clientIds: filter.clientIds,
  types: filter.types?.map(mapOverheadTypeToDTO),
  statuses: filter.statuses?.map(mapOverheadStatusToDTO),
});
